/**
 * WebMCP (decision 0123): the web app registers the external MCP's person tools with the browser's
 * `document.modelContext`, so a browser agent acting for the signed-in person can use them on app.minga.run. The tools
 * are the same table `/mcp` serves (apps/cloud/src/mcp/tools.ts `personToolList`); the browser calls them over the
 * same-origin, session-authenticated `/api/webmcp/*` endpoints.
 *
 * This file holds the per-tool WebMCP hints (spec `ToolAnnotations`: `readOnlyHint`, `consequentialHint`,
 * `untrustedContentHint`), next to the MCP annotations in `PERSON_TOOL_META` / `DOCUMENT_TOOL_META`, and the wire
 * shapes of the two endpoints. Hints only: neither the browser nor Minga trusts them for safety; the tools' own rights
 * checks are the same as over `/mcp`.
 */
import { Schema } from "effect"
import type { DocumentToolName } from "./documents"
import type { KnowledgeToolName } from "./knowledge-search"
import type { PersonToolName } from "./mcp"

/** Every tool the external MCP lists: the person tools, the knowledge tools and the document tools. */
export type ExternalToolName = PersonToolName | KnowledgeToolName | DocumentToolName

/** WebMCP `ToolAnnotations` (Draft CG Report, 16 Oct 2026). `debugging` is never set for Minga's tools. */
export interface WebMcpToolAnnotations {
  readonly readOnlyHint: boolean
  readonly consequentialHint: boolean
  readonly untrustedContentHint: boolean
}

interface Hints {
  /** Significant or hard to undo, or acting outside the project (a browser may ask the person first). */
  readonly consequential: boolean
  /** The answer carries text people or agents wrote (chat, documents, titles, questions, replies). */
  readonly untrusted: boolean
}

const READ_UNTRUSTED: Hints = { consequential: false, untrusted: true }
const READ_OWN: Hints = { consequential: false, untrusted: false }
const WRITE: Hints = { consequential: false, untrusted: false }
const CONSEQUENTIAL: Hints = { consequential: true, untrusted: false }

/**
 * The hints of every external tool. A `Record` over every name, so a new tool can't be listed without a decision here.
 * Consequential: every tool MCP marks destructive (plan, undo_plan, close_item, set_routing, set_workers,
 * post_document), plus the ones that decide, stop, start or reach outside: answer (cards), cancel_work, request_work,
 * attach_repo, create_project, set_subscription_account. Untrusted: every read whose answer carries written text
 * (`guide` is Minga's own skill text), and ask_lead (the lead's reply).
 */
export const WEBMCP_TOOL_HINTS: Readonly<Record<ExternalToolName, Hints>> = {
  // reads
  list_projects: READ_UNTRUSTED,
  read_project: READ_UNTRUSTED,
  read_item: READ_UNTRUSTED,
  read_work: READ_UNTRUSTED,
  needs_me: READ_UNTRUSTED,
  list_notifications: READ_UNTRUSTED,
  list_runners: READ_UNTRUSTED,
  search: READ_UNTRUSTED,
  read: READ_UNTRUSTED,
  look_back: READ_UNTRUSTED,
  list_documents: READ_UNTRUSTED,
  guide: READ_OWN,
  // writes inside a project
  capture: WRITE,
  comment: WRITE,
  record_kpi: WRITE,
  attach: WRITE,
  link_work: WRITE,
  verify_work: WRITE,
  remember: WRITE,
  mark_notifications_read: WRITE,
  ask_lead: { consequential: false, untrusted: true },
  // decision 0142: messaging (read_messages carries agents' words; connecting a client lets agents message it)
  message: WRITE,
  read_messages: { consequential: false, untrusted: true },
  connect_participant: CONSEQUENTIAL,
  // consequential
  plan: CONSEQUENTIAL,
  undo_plan: CONSEQUENTIAL,
  close_item: CONSEQUENTIAL,
  answer: CONSEQUENTIAL,
  cancel_work: CONSEQUENTIAL,
  request_work: CONSEQUENTIAL,
  attach_repo: CONSEQUENTIAL,
  set_routing: CONSEQUENTIAL,
  set_workers: CONSEQUENTIAL,
  create_project: CONSEQUENTIAL,
  set_subscription_account: CONSEQUENTIAL,
  post_document: CONSEQUENTIAL,
}

const isExternalTool = (name: string): name is ExternalToolName => Object.hasOwn(WEBMCP_TOOL_HINTS, name)

/**
 * A tool's WebMCP annotations from its MCP annotations: `readOnlyHint` is MCP's; a destructive tool is always
 * consequential; an unknown name gets the cautious default (consequential, untrusted).
 */
export const webMcpAnnotationsOf = (
  name: string,
  mcp: { readonly readOnlyHint: boolean; readonly destructiveHint?: boolean | undefined },
): WebMcpToolAnnotations => {
  const hints = isExternalTool(name) ? WEBMCP_TOOL_HINTS[name] : { consequential: true, untrusted: true }
  return {
    readOnlyHint: mcp.readOnlyHint,
    consequentialHint: !mcp.readOnlyHint && (hints.consequential || mcp.destructiveHint === true),
    untrustedContentHint: hints.untrusted,
  }
}

/** WebMCP tool names: 1–128 of `[A-Za-z0-9_.-]` (registerTool rejects anything else). */
export const WEBMCP_TOOL_NAME = /^[A-Za-z0-9_.-]{1,128}$/

/** `GET /api/webmcp/tools`: one entry per tool, ready for `registerTool` (minus `execute`). */
export const WebMcpToolEntry = Schema.Struct({
  name: Schema.String.check(Schema.isPattern(WEBMCP_TOOL_NAME)),
  title: Schema.String,
  description: Schema.String,
  inputSchema: Schema.Record(Schema.String, Schema.Unknown),
  annotations: Schema.Struct({
    readOnlyHint: Schema.Boolean,
    consequentialHint: Schema.Boolean,
    untrustedContentHint: Schema.Boolean,
  }),
})
export type WebMcpToolEntry = typeof WebMcpToolEntry.Type

export const WebMcpTools = Schema.Struct({ tools: Schema.Array(WebMcpToolEntry) })
export type WebMcpTools = typeof WebMcpTools.Type

/** `POST /api/webmcp/call` body. */
export const WebMcpCallRequest = Schema.Struct({
  name: Schema.String.check(Schema.isPattern(WEBMCP_TOOL_NAME)),
  arguments: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
})
export type WebMcpCallRequest = typeof WebMcpCallRequest.Type

/** `POST /api/webmcp/call` answer: the MCP `tools/call` result shape (text blocks, the whole answer, isError). */
export const WebMcpCallResult = Schema.Struct({
  isError: Schema.Boolean,
  content: Schema.Array(Schema.Struct({ type: Schema.Literal("text"), text: Schema.String })),
  structuredContent: Schema.Record(Schema.String, Schema.Unknown),
})
export type WebMcpCallResult = typeof WebMcpCallResult.Type
